import { downloadBlob } from "@/utils/downloadFile";

export type AnalyticsExportFormat = "csv" | "xlsx";

export type AnalyticsExportTableSection = {
  title: string;
  headers: string[];
  rows: Array<Array<string | number | null | undefined>>;
  summaryRows?: Array<Array<string | number | null | undefined>>;
};

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value == null) {
    return "";
  }

  const text = String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function escapeHtmlCell(value: string | number | null | undefined): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function buildSectionsCsv(
  sections: AnalyticsExportTableSection[],
): string {
  const lines: string[] = [];

  for (const section of sections) {
    lines.push(section.title);
    lines.push(section.headers.map(escapeCsvCell).join(","));
    for (const row of section.rows) {
      lines.push(row.map(escapeCsvCell).join(","));
    }
    for (const row of section.summaryRows ?? []) {
      lines.push(row.map(escapeCsvCell).join(","));
    }
    lines.push("");
  }

  return `${lines.join("\n")}\n`;
}

function buildSectionHtmlTable(section: AnalyticsExportTableSection): string {
  const headerCells = section.headers
    .map((header) => `<th>${escapeHtmlCell(header)}</th>`)
    .join("");
  const bodyRows = section.rows
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td>${escapeHtmlCell(cell)}</td>`)
          .join("")}</tr>`,
    )
    .join("");
  const summaryRows = (section.summaryRows ?? [])
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td>${escapeHtmlCell(cell)}</td>`)
          .join("")}</tr>`,
    )
    .join("");
  const tableFooter = summaryRows ? `<tfoot>${summaryRows}</tfoot>` : "";

  return `
    <h3 style="font-family: sans-serif; margin: 16px 0 8px;">${escapeHtmlCell(section.title)}</h3>
    <table>
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
      ${tableFooter}
    </table>
  `;
}

export function buildSectionsExcelHtml(
  sections: AnalyticsExportTableSection[],
): string {
  const tables = sections.map(buildSectionHtmlTable).join("");

  return `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8" />
        <style>
          table { border-collapse: collapse; width: 100%; margin-bottom: 8px; }
          th, td { border: 1px solid #d4d4d8; padding: 8px; text-align: left; }
          th { background: #f4f4f5; font-weight: 700; }
          tfoot td { background: #e4e4e7; border-top: 2px solid #71717a; font-weight: 700; }
        </style>
      </head>
      <body>${tables}</body>
    </html>
  `.trim();
}

export function downloadAnalyticsExport(
  format: AnalyticsExportFormat,
  sections: AnalyticsExportTableSection[],
  filenameBase: string,
): void {
  if (
    sections.every(
      (section) =>
        section.rows.length === 0 && (section.summaryRows?.length ?? 0) === 0,
    )
  ) {
    return;
  }

  if (format === "csv") {
    const csv = buildSectionsCsv(sections);
    downloadBlob(
      new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" }),
      `${filenameBase}.csv`,
    );
    return;
  }

  const html = buildSectionsExcelHtml(sections);
  downloadBlob(
    new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" }),
    `${filenameBase}.xlsx`,
  );
}
